import { datadogLogs } from '@datadog/browser-logs';
import React from 'react';
import { snapshot } from 'valtio';
import { logger } from 'logging-utils';
import { AppPages, navigate } from '../../common/routing/app-navigator';
import {
  findConnectorById,
  loadConnectorResources,
  monitorConnectorResources,
} from '../../connectors/api/connectors-api';
import {
  getHubSpotFieldForSourceControlRepos,
  submitHubSpotForm,
} from '../../hubspot-analytics/actions/hubspot-analytics-actions';
import { HubSpotFormsIds } from '../../hubspot-analytics/hubspot-types';
import { scanAll } from '../../new-scan/actions/scan-actions';
import { ControlObject, NodeTypes, RepoNode } from '../onboarding-types';
import { setOnboardingError } from '../store-actions/onboarding-store-actions';
import {
  setFilteredRepoTreeData,
  setIsChooseAllSelected,
  setRepoTreeData,
  setSearchValue,
} from '../store-actions/repo-selection-store-actions';
import RepoSelectionStore from '../stores/onboarding-repo-selection-store';
import OnboardingStore from '../stores/onboarding-store';

export const createAppSelectionControl = (treeData: RepoNode[], level = 0) => {
  treeData.forEach(node => {
    setControlObject(node.id, node.isMonitored, level === 0, node.resourceType);
    node.children?.length && createAppSelectionControl(node.children);
  });
};

export const handleChooseAll = (isSelected: boolean) => {
  setIsChooseAllSelected(isSelected);
  const { controlObject } = snapshot(RepoSelectionStore);
  const newControlObject = {};
  Object.keys(controlObject).forEach(id => {
    newControlObject[id] = {
      ...controlObject[id],
      selected: isSelected,
    };
  });
  RepoSelectionStore.controlObject = newControlObject;
};

export const initRepoSelectionStep = async (
  selectedConnectorId: string,
  disableCache?: boolean,
  useSavedData?: boolean,
) => {
  const resources = await loadConnectorResources(
    {
      connectorID: selectedConnectorId,
    },
    disableCache,
  );
  if (resources) {
    datadogLogs.logger.info(`Loading connector resources was successful`);
    createAppSelectionControl(resources);
    setRepoTreeData(resources);
    setFilteredRepoTreeData(resources);
    if (!useSavedData) {
      setIsChooseAllSelected(true);
    }
  } else {
    datadogLogs.logger.info(
      `Loading connector resources failed, setting an empty array`,
    );
    setRepoTreeData([]);
    setFilteredRepoTreeData([]);
  }
};

export const searchRepos = (searchString: string) => {
  const { repoTreeData } = snapshot(RepoSelectionStore);
  setSearchValue(searchString);
  if (!repoTreeData) {
    return;
  }
  if (!searchString) {
    setFilteredRepoTreeData(repoTreeData);
    return;
  }
  const filtered = filterTreeData(repoTreeData, searchString);
  expandAll();
  setFilteredRepoTreeData(filtered);
};

const filterTreeData = (treeData: RepoNode[], searchString: string) => {
  const filterFunction = (node: RepoNode) => {
    return node.name.includes(searchString);
  };

  const filtered = treeData.reduce((acc: RepoNode[], node) => {
    if (node.children.length > 0) {
      const filteredChildren = filterTreeData([...node.children], searchString);
      if (filteredChildren.length > 0) {
        acc.push({ ...node, children: filteredChildren });
      } else if (filterFunction(node)) {
        acc.push(node);
      }
    } else if (filterFunction(node)) {
      acc.push(node);
    }
    return acc;
  }, []);
  return filtered;
};

const createInputArray = (
  treeData: RepoNode[],
  controlObject: ControlObject,
) => {
  return treeData.reduce((flattenedTree, node) => {
    const input = {
      id: node.id,
      resourceType: node.resourceType,
      name: node.name,
      isMonitored: !!controlObject[node.id].selected,
    };
    if (
      controlObject[node.id].selected &&
      node.resourceType === NodeTypes.Edge
    ) {
      return [
        ...flattenedTree,
        input,
        ...createInputArray(node.children, controlObject),
      ];
    }

    return [
      ...flattenedTree,
      ...createInputArray(node.children, controlObject),
    ];
  }, [] as RepoNode[]);
};

export const onContinueClick = async (
  e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  connectorId?: string,
  skipDiscovery?: boolean,
) => {
  const {
    repoTreeData,
    controlObject,
    isChooseAllSelected,
    monitorAllNewlyCreatedResources,
    checkedReposUtils: { checkedReposNumber, reposFound },
  } = snapshot(RepoSelectionStore);
  const { chosenCodeRepo: chosenConnector } = snapshot(OnboardingStore);
  const chosenCodeRepoId = chosenConnector?.id || connectorId;
  if (!chosenCodeRepoId || !controlObject) {
    setOnboardingError(
      true,
      'Oops! We encountered an error.. Please refresh the page',
    );
    datadogLogs.logger.error(
      'Onboarding: repo selection continue failed due to no chosenCodeRepo or repoCheckboxControlObj',
    );
    return;
  }
  datadogLogs.logger.info(
    `Onboarding: user selected ${checkedReposNumber} repos out of ${reposFound}`,
    { reposFound, repoSelected: checkedReposNumber },
  );

  const response = await monitorConnectorResources({
    connectorID: chosenCodeRepoId,
    monitorAllResources: isChooseAllSelected || undefined,
    monitorAllNewlyCreatedResources: monitorAllNewlyCreatedResources
      ? new Date().getTime()
      : undefined,
    resources: !isChooseAllSelected
      ? createInputArray(repoTreeData!, controlObject)
      : null,
  });

  if (!response) {
    setOnboardingError(
      true,
      'Oops! We encountered an error.. Please refresh the page',
    );
    datadogLogs.logger.error(
      'Failed to set selected repos, received an invalid response',
    );
    return;
  }
  datadogLogs.logger.info('Onboarding: Successfully selected repos to monitor');
  if (!skipDiscovery) {
    scanAll();
    navigate(AppPages.Dashboard);
  }
  const connector = chosenConnector || findConnectorById(chosenCodeRepoId);
  if (connector) {
    const repoCountField = getHubSpotFieldForSourceControlRepos(
      connector.displayName,
    );
    if (!repoCountField) {
      logger.error(`Could not find repoCountField of ${connector.displayName}`);
    } else {
      submitHubSpotForm(
        {
          [repoCountField]: `${checkedReposNumber} / ${reposFound}`,
        },
        HubSpotFormsIds.OxAppForm,
      );
    }
  }
  return response;
};

export const toggleExpand = (id: string) => {
  const { controlObject } = snapshot(RepoSelectionStore);
  const copy = { ...controlObject[id] };
  RepoSelectionStore.controlObject[id] = {
    ...copy,
    expanded: !controlObject[id]?.expanded,
  };
};

const select = (
  id: string,
  value: boolean,
  children: RepoNode[],
  newObject: ControlObject,
) => {
  newObject[id] = {
    ...newObject[id],
    selected: value,
  };
  children?.forEach(node => {
    return select(node.id, value, node.children, newObject);
  });
  return newObject;
};

export const toggleSelection = (
  id: string,
  value: boolean,
  children: RepoNode[],
) => {
  const { controlObject } = snapshot(RepoSelectionStore);
  let newObject = { ...controlObject };
  newObject = select(id, value, children, newObject);
  RepoSelectionStore.controlObject = newObject;
};

export const toggleSingleSelection = (id: string, value: boolean) => {
  const { controlObject } = snapshot(RepoSelectionStore);
  const copy = { ...controlObject[id] };
  RepoSelectionStore.controlObject[id] = {
    ...copy,
    selected: value,
  };
};

export const expandAll = () => {
  const { controlObject } = snapshot(RepoSelectionStore);
  const newObject = {};
  Object.keys(newObject).forEach(id => {
    newObject[id] = {
      ...controlObject[id],
      expanded: true,
    };
  });
};

export const setControlObject = (
  id: string,
  selected: boolean,
  expanded: boolean,
  nodeType: NodeTypes,
) => {
  RepoSelectionStore.controlObject[id] = {
    selected,
    expanded,
    nodeType,
  };
};
